import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { useMemo } from 'react';
import { EllipsesMenu } from 'src/components';
import { sidePanelActionButtonSx } from 'src/components/SidePanelRow';
import { useProjectActions } from 'src/components/SidePanelTabs/Projects/use-project-actions';
import { useSiteActions } from 'src/components/SiteComponents/use-site-actions';
import { Address, Project, useDeleteProject } from 'src/graphql';
import { useConfirmationModal } from 'src/utils/hooks';

interface SiteMenuProps {
  address: Address;
  project: Project;
  onDelete?: () => void;
}

export const ProjectMenu = ({ address, project, onDelete }: SiteMenuProps) => {
  const { confirm } = useConfirmationModal();
  const { deleteProject } = useDeleteProject();

  const { updateSite, copySiteId, copySiteUrl, changeSites, disassociateSite } =
    useSiteActions({
      address,
    });

  const { copyProjectId, copyProjectUrl } = useProjectActions({ address, project });

  const menuOptions = useMemo(
    () =>
      [
        // a deleted project's card is read-only, so only the copy actions remain
        ...(project?.isDeleted ? [] : [updateSite, changeSites]),
        copySiteId,
        copySiteUrl,
        copyProjectId,
        copyProjectUrl,
        ...(project?.isDeleted ? [] : [disassociateSite]),
        {
          label: 'Delete',
          icon: 'trash-2',
          iconLibrary: 'lucide' as const,
          isAllowed: !!project?.id && !project.isDeleted,
          danger: true,
          handler: async () => {
            const confirmed = await confirm({
              title: 'Delete project?',
              body: 'The project will be removed from this site.',
              confirmLabel: 'Delete project',
              danger: true,
            });
            if (confirmed) {
              const { errors } = await deleteProject(project?.id);
              if (errors) {
                console.error(errors);
                return;
              }
              if (onDelete) {
                onDelete();
              }
            }
          },
        },
      ]
        .filter((option) => ('isAllowed' in option ? option.isAllowed : true))
        .map((option) => ({
          ...option,
          onClick: option.handler,
        })),
    [
      updateSite,
      changeSites,
      disassociateSite,
      copySiteId,
      copySiteUrl,
      copyProjectId,
      copyProjectUrl,
      project?.id,
      project?.isDeleted,
      confirm,
      deleteProject,
      onDelete,
    ],
  );

  if (!menuOptions.length) {
    return null;
  }

  return (
    <V2ThemeProvider>
      <EllipsesMenu
        options={menuOptions}
        icon="more-vertical"
        iconLibrary="lucide"
        sx={sidePanelActionButtonSx}
      />
    </V2ThemeProvider>
  );
};
